import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Activity, Zap, Database, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, subHours } from "date-fns";

interface SystemHealth {
  aiUsageLast24h: number;
  aiErrorsLast24h: number;
  aiSuccessRate: number;
  queuePending: number;
  queueProcessing: number;
  queueCompleted24h: number;
  queueFailed24h: number;
  cacheHitRate: number;
  recentErrors: {
    id: string;
    channel: string;
    error_message: string | null;
    created_at: string;
  }[];
}

export function SystemHealthPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SystemHealth | null>(null);

  useEffect(() => {
    loadSystemHealth();
  }, []);

  const loadSystemHealth = async () => {
    try {
      const last24h = subHours(new Date(), 24).toISOString();

      // AI Usage last 24h
      const { count: aiUsageLast24h } = await supabase
        .from("ai_usage_logs")
        .select("*", { count: "exact", head: true })
        .gte("created_at", last24h);

      // AI Errors last 24h
      const { count: aiErrorsLast24h } = await supabase
        .from("ai_usage_logs")
        .select("*", { count: "exact", head: true })
        .gte("created_at", last24h)
        .eq("status", "error");

      // Queue pending
      const { count: queuePending } = await supabase
        .from("analysis_job_queue")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending");

      // Queue processing
      const { count: queueProcessing } = await supabase
        .from("analysis_job_queue")
        .select("*", { count: "exact", head: true })
        .eq("status", "processing");

      // Queue completed 24h
      const { count: queueCompleted24h } = await supabase
        .from("analysis_job_queue")
        .select("*", { count: "exact", head: true })
        .gte("completed_at", last24h)
        .eq("status", "completed");

      // Queue failed 24h
      const { count: queueFailed24h } = await supabase
        .from("analysis_job_queue")
        .select("*", { count: "exact", head: true })
        .gte("created_at", last24h)
        .eq("status", "failed");

      // Cache stats
      const { count: cacheTotal } = await supabase
        .from("analysis_cache")
        .select("*", { count: "exact", head: true });

      const { data: cacheHits } = await supabase
        .from("analysis_cache")
        .select("hit_count")
        .gt("hit_count", 1);

      const totalHits = cacheHits?.reduce((acc, c) => acc + (c.hit_count - 1), 0) || 0;
      const cacheHitRate = cacheTotal && cacheTotal > 0 
        ? Math.round((totalHits / (totalHits + (cacheTotal || 0))) * 100) 
        : 0;

      // Recent errors
      const { data: recentErrors } = await supabase
        .from("ai_usage_logs")
        .select("id, channel, error_message, created_at")
        .eq("status", "error")
        .order("created_at", { ascending: false })
        .limit(5);

      const total = aiUsageLast24h || 0;
      const errors = aiErrorsLast24h || 0;
      const aiSuccessRate = total > 0 ? Math.round(((total - errors) / total) * 100) : 100;

      setData({
        aiUsageLast24h: total,
        aiErrorsLast24h: errors,
        aiSuccessRate,
        queuePending: queuePending || 0,
        queueProcessing: queueProcessing || 0,
        queueCompleted24h: queueCompleted24h || 0,
        queueFailed24h: queueFailed24h || 0,
        cacheHitRate,
        recentErrors: recentErrors || [],
      });
    } catch (error) {
      console.error("Error loading system health:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Failed to load system health
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = (rate: number) => {
    if (rate >= 95) return "text-green-500";
    if (rate >= 80) return "text-yellow-500";
    return "text-red-500";
  };

  return (
    <div className="space-y-6">
      {/* Overall System Status */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              AI Success Rate
            </CardDescription>
            <CardTitle className={`text-3xl ${getStatusColor(data.aiSuccessRate)}`}>
              {data.aiSuccessRate}%
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {data.aiUsageLast24h} requests / {data.aiErrorsLast24h} errors (24h)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Queue Status
            </CardDescription>
            <CardTitle className="text-3xl">{data.queuePending + data.queueProcessing}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 text-sm">
              <Badge variant="outline">{data.queuePending} pending</Badge>
              <Badge variant="secondary">{data.queueProcessing} processing</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Processed (24h)
            </CardDescription>
            <CardTitle className="text-3xl">{data.queueCompleted24h}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {data.queueFailed24h > 0 && (
                <span className="text-red-500">{data.queueFailed24h} failed</span>
              )}
              {data.queueFailed24h === 0 && "All successful"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              Cache Hit Rate
            </CardDescription>
            <CardTitle className={`text-3xl ${getStatusColor(data.cacheHitRate)}`}>
              {data.cacheHitRate}%
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Analysis cache efficiency</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Errors */}
      {data.recentErrors.length > 0 && (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              Recent Errors
            </CardTitle>
            <CardDescription>Last 5 AI processing errors</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.recentErrors.map((error) => (
                <Alert key={error.id} variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle className="flex items-center justify-between">
                    <span>{error.channel}</span>
                    <span className="text-xs font-normal flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(error.created_at), "MMM d, h:mm a")}
                    </span>
                  </AlertTitle>
                  <AlertDescription className="text-sm">
                    {error.error_message || "Unknown error"}
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {data.recentErrors.length === 0 && (
        <Alert className="border-green-500/50 bg-green-50/50">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <AlertTitle className="text-green-700">All Systems Operational</AlertTitle>
          <AlertDescription className="text-green-600">
            No errors in the last 24 hours
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
